/* AutoMood theme — cart drawer, quick add, buy now, variants, small UI */
(() => {
  const AM = window.AM || {};
  const root = AM.routes?.root || '/';
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const money = (cents) => {
    const value = cents / 100;
    return (AM.moneyFormat || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) =>
      key.includes('no_decimals')
        ? Math.round(value).toLocaleString('en-US')
        : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  };

  const priceHTML = (price, compare, { save = false } = {}) => {
    const sale = compare > price;
    return (
      `<span class="am-price${sale ? ' am-price--sale' : ''}">` +
      (sale ? `<s class="am-price__was"><span class="am-sr">Was </span>${money(compare)}</s>` : '') +
      `<span class="am-price__now">${money(price)}</span></span>` +
      (sale && save ? `<span class="am-save">Save ${money(compare - price)}</span>` : '')
    );
  };

  const api = async (path, body) => {
    const res = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  };

  /* ---------- Cart drawer ---------- */
  const drawer = $('#am-cart');
  const shuffle = (list) => {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  };
  const upsellPool = shuffle(JSON.parse($('#am-upsell-json')?.textContent || '[]'));
  let lastFocus = null;

  const cart = {
    data: null,

    async refresh() {
      this.render(await api('cart.js'));
    },

    async add(id, quantity = 1) {
      await api('cart/add.js', { items: [{ id: Number(id), quantity: Number(quantity) || 1 }] });
      await this.refresh();
    },

    async change(key, quantity) {
      const data = await api('cart/change.js', { id: key, quantity });
      this.render(data);
      const line = data.items.find((i) => i.key === key);
      if (line && line.quantity < quantity) this.notice(key, 'Quantity adjusted to what we have available');
    },

    notice(key, text) {
      const btn = drawer && $(`[data-am-line="${CSS.escape(key)}"]`, drawer);
      const row = btn?.closest('.am-line');
      if (!row) return;
      $('.am-line__limit', row)?.remove();
      row.querySelector('.am-line__meta').insertAdjacentHTML('afterend', `<div class="am-line__limit">${esc(text)}</div>`);
    },

    open() {
      if (!drawer) return (location.href = AM.routes.cart);
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('am-lock');
      $('.am-drawer__panel', drawer).focus({ preventScroll: true });
    },

    close() {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('am-lock');
      lastFocus?.focus?.({ preventScroll: true });
    },

    render(data) {
      const bumped = this.data && data.item_count !== this.data.item_count;
      this.data = data;
      // While an item is flying, wait for it to land before the count changes
      const showCount = () => $$('[data-am-cart-count]').forEach((el) => {
        el.textContent = data.item_count;
        el.hidden = data.item_count === 0;
        if (bumped) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
      flight ? flight.then(showCount) : showCount();
      if (!drawer) return;

      const empty = data.item_count === 0;
      $('[data-am-cart-items]', drawer).innerHTML = empty
        ? `<li class="am-drawer__empty">
             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6.4 8h11.2a1 1 0 0 1 1 .9l.9 10.5a1.5 1.5 0 0 1-1.5 1.6H6a1.5 1.5 0 0 1-1.5-1.6l.9-10.5a1 1 0 0 1 1-.9Z"/><path d="M9 10.5V7a3 3 0 0 1 6 0v3.5"/></svg>
             <p class="am-h3">Your cart is empty</p>
             <p class="am-muted">Find something to upgrade your ride.</p>
             <a class="am-btn am-btn--dark am-btn--sm" href="${esc(AM.routes.all)}">Start shopping</a>
           </li>`
        : data.items.map((item) => `
           <li class="am-line">
             <a href="${esc(item.url)}">${item.image ? `<img src="${esc(item.image)}" alt="" width="76" height="76" loading="lazy">` : ''}</a>
             <div>
               <a class="am-line__title" href="${esc(item.url)}">${esc(item.product_title)}</a>
               ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="am-line__variant">${esc(item.variant_title)}</div>` : ''}
               ${(item.line_level_discount_allocations || []).map((d) => `<div class="am-line__deal">${esc(d.discount_application.title)} (−${money(d.amount)})</div>`).join('')}
               <div class="am-line__meta">
                 <div class="am-qty am-qty--sm">
                   <button type="button" aria-label="Decrease quantity" data-am-line="${esc(item.key)}" data-qty="${item.quantity - 1}">−</button>
                   <output>${item.quantity}</output>
                   <button type="button" aria-label="Increase quantity" data-am-line="${esc(item.key)}" data-qty="${item.quantity + 1}">+</button>
                 </div>
                 <button type="button" class="am-line__remove" data-am-line="${esc(item.key)}" data-qty="0">Remove</button>
               </div>
             </div>
             <div class="am-line__price">${item.original_line_price > item.final_line_price
               ? `<s>${money(item.original_line_price)}</s><span class="is-sale">${money(item.final_line_price)}</span>`
               : money(item.final_line_price)}</div>
           </li>`).join('');

      // Free delivery progress
      const ship = $('[data-am-ship]', drawer);
      const goal = Number(AM.freeShipping) || 0;
      ship.hidden = empty || !goal;
      if (goal) {
        const left = goal - data.total_price;
        ship.classList.toggle('is-done', left <= 0);
        $('[data-am-ship-text]', ship).innerHTML = left > 0
          ? `Add <b>${money(left)}</b> more for free delivery`
          : '<b>You unlocked free delivery</b>';
        $('i', ship).style.width = Math.min(100, (data.total_price / goal) * 100) + '%';
      }

      // Upsell: suggest items that aren't in the cart yet
      const inCart = new Set(data.items.map((i) => i.product_id));
      const picks = empty ? [] : upsellPool.filter((p) => p.available && !inCart.has(p.id)).slice(0, 3);
      const upsell = $('[data-am-upsell]', drawer);
      upsell.hidden = picks.length === 0;
      $('[data-am-upsell-list]', upsell).innerHTML = picks.map((p) => `
        <div class="am-upsell__item">
          <a href="${esc(p.url)}">${p.image ? `<img src="${esc(p.image)}" alt="" width="56" height="56" loading="lazy">` : ''}</a>
          <div><strong>${esc(p.title)}</strong>${priceHTML(p.price, p.compare_at_price)}</div>
          <button type="button" class="am-btn am-btn--light am-btn--sm" data-am-quick-add="${p.variant_id}">Add</button>
        </div>`).join('');

      $('[data-am-cart-foot]', drawer).hidden = empty;
      // Savings from compare-at prices and Discounts-tab discounts
      const saved = data.items.reduce((n, i) => n + Math.max(0, (i.compare_at_price || 0) - i.original_price) * i.quantity, 0) + (data.total_discount || 0);
      const savings = $('[data-am-savings]', drawer);
      savings.hidden = saved <= 0;
      $('strong', savings).textContent = '−' + money(saved);
      $$('[data-am-cart-total]').forEach((el) => (el.textContent = money(data.total_price)));
    },
  };

  // Added items fly into the header cart button in a quick arc; the drawer only opens when the cart is clicked
  let flight = null;
  const bumpCart = () => {
    $$('.am-cartbtn').forEach((b) => {
      b.classList.remove('is-hit');
      void b.offsetWidth;
      b.classList.add('is-hit');
    });
  };
  const flyToCart = (img) => {
    const target = $$('.am-cartbtn').find((b) => b.offsetParent);
    if (!target) return;
    if (!img || !img.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return bumpCart();
    const from = img.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const size = 56;
    const x = document.createElement('div');
    const y = document.createElement('div');
    const thumb = img.cloneNode(true);
    ['id', 'srcset', 'sizes', 'loading'].forEach((a) => thumb.removeAttribute(a));
    if (thumb.tagName === 'IMG') thumb.alt = '';
    else thumb.setAttribute('aria-hidden', 'true');
    x.className = 'am-fly';
    thumb.setAttribute('class', 'am-fly__img');
    Object.assign(x.style, { left: `${from.left + from.width / 2 - size / 2}px`, top: `${from.top + from.height / 2 - size / 2}px` });
    y.append(thumb);
    x.append(y);
    document.body.append(x);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const start = Math.max(from.width, from.height) / size;
    const duration = 640;
    // X glides evenly while Y rises fast then settles, which draws a clean arc
    x.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${dx}px)` }], { duration, easing: 'cubic-bezier(.45, 0, .55, 1)' });
    y.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${dy}px)` }], { duration, easing: 'cubic-bezier(.2, .75, .35, 1)' });
    const shrink = thumb.animate([
      { transform: `scale(${start})`, borderRadius: '22%', opacity: 1 },
      { transform: 'scale(1)', borderRadius: '50%', opacity: 1, offset: 0.3 },
      { transform: 'scale(.5)', borderRadius: '50%', opacity: 1, offset: 0.85 },
      { transform: 'scale(.15)', borderRadius: '50%', opacity: 0 },
    ], { duration, easing: 'cubic-bezier(.3, 0, .2, 1)' });
    flight = new Promise((done) => {
      shrink.onfinish = () => {
        x.remove();
        bumpCart();
        flight = null;
        done();
      };
    });
  };

  // Card quick add: the + turns into a small − 1 + stepper, then folds back
  const qaTimers = new WeakMap();
  const inCart = (id) => (cart.data?.items || []).filter((i) => String(i.variant_id) === String(id));
  const qaOpen = (qa, qty) => {
    $('output', qa).textContent = qty;
    qa.classList.add('is-open');
    $$('.am-qa__step button', qa).forEach((b) => (b.tabIndex = 0));
    clearTimeout(qaTimers.get(qa));
    qaTimers.set(qa, setTimeout(() => qaClose(qa), 2600));
  };
  const qaClose = (qa) => {
    qa.classList.remove('is-open');
    $$('.am-qa__step button', qa).forEach((b) => (b.tabIndex = -1));
  };
  const openPicker = (qa) => {
    $$('.am-card.is-picking').forEach((c) => c !== qa && c.classList.remove('is-picking'));
    const variants = JSON.parse(qa.dataset.amVariants);
    $('[data-am-pick-opts]', qa).innerHTML = variants
      .map((v) => `<button type="button" data-am-pick="${v.id}"${v.a ? '' : ' disabled'}>${esc(v.t)}</button>`)
      .join('');
    qa.classList.add('is-picking');
  };
  // The bar updates instantly; Shopify gets one request after the taps stop
  const qaState = new WeakMap(); // qa -> { qty, timer, busy, again }
  const qaQty = (qa) => {
    const st = qaState.get(qa);
    if (st && st.id === qa.dataset.amQa) return st.qty;
    return inCart(qa.dataset.amQa).reduce((n, i) => n + i.quantity, 0);
  };
  const qaSet = (qa, qty) => {
    const id = qa.dataset.amQa;
    const st = qaState.get(qa)?.id === id ? qaState.get(qa) : { id };
    st.qty = qty;
    qaState.set(qa, st);
    if (qty > 0) qaOpen(qa, qty);
    else qaClose(qa);
    clearTimeout(st.timer);
    st.timer = setTimeout(() => qaSync(qa, st), 350);
  };
  // The counter keeps the number the shopper picked; Shopify quietly keeps only what's in stock,
  // so the real quantity is only seen in the cart and at checkout
  const qaSync = async (qa, st) => {
    if (st.busy) return (st.again = true);
    st.busy = true;
    const wanted = st.qty;
    try {
      cart.render(await api('cart/update.js', { updates: { [st.id]: wanted } }));
    } catch (err) {
      await cart.refresh().catch(() => {});
    }
    st.busy = false;
    if (st.again) {
      st.again = false;
      return qaSync(qa, st);
    }
  };

  const withLoading = async (btn, task) => {
    btn.classList.add('is-loading');
    try {
      await task();
    } finally {
      btn.classList.remove('is-loading');
    }
  };

  /* ---------- Clicks ---------- */
  document.addEventListener('click', (e) => {
    const hit = (sel) => e.target.closest(sel);
    let el;
    if (!hit('.am-pick, [data-am-qa-add]')) $$('.am-card.is-picking').forEach((c) => c.classList.remove('is-picking'));

    if ((el = hit('[data-am-cart-open]'))) {
      e.preventDefault();
      cart.open();
    } else if (hit('[data-am-cart-close]')) {
      cart.close();
    } else if ((el = hit('[data-am-pick]'))) {
      // Option chosen on a card: remember that variant and add it
      const qa = el.closest('[data-am-qa]');
      qa.dataset.amQa = el.dataset.amPick;
      qa.dataset.amPicked = '1';
      qa.classList.remove('is-picking');
      $('[data-am-qa-add]', qa).click();
    } else if ((el = hit('[data-am-qa-add]'))) {
      const qa = el.closest('[data-am-qa]');
      if (qa.dataset.amVariants && !qa.dataset.amPicked) {
        openPicker(qa);
        return;
      }
      delete qa.dataset.amPicked;
      flyToCart($('.am-card__media img, .am-card__media svg', qa));
      qaSet(qa, qaQty(qa) + 1);
    } else if ((el = hit('[data-am-qa-step]'))) {
      const qa = el.closest('[data-am-qa]');
      qaSet(qa, Math.max(0, qaQty(qa) + Number(el.dataset.amQaStep)));
    } else if ((el = hit('[data-am-quick-add]'))) {
      e.preventDefault();
      const inDrawer = !!el.closest('#am-cart');
      if (!inDrawer) flyToCart($('img', el.closest('.am-card, .am-upsell__item')));
      withLoading(el, () => cart.add(el.dataset.amQuickAdd))
        .then(() => {
          el.classList.add('is-done');
          setTimeout(() => el.classList.remove('is-done'), 1200);
        })
        .catch((err) => alert(err.message));
    } else if ((el = hit('[data-am-line]'))) {
      cart.change(el.dataset.amLine, Number(el.dataset.qty)).catch((err) => alert(err.message));
    } else if ((el = hit('[data-am-step]'))) {
      const input = $('input', el.closest('.am-qty'));
      const next = Math.max(1, (Number(input.value) || 1) + Number(el.dataset.amStep));
      input.value = input.max ? Math.min(next, Number(input.max)) : next;
    } else if ((el = hit('[data-am-mood]'))) {
      el.closest('.am-hero').style.setProperty('--mood', el.dataset.amMood);
      $$('[data-am-mood]').forEach((b) => b.setAttribute('aria-pressed', b === el));
    } else if ((el = hit('[data-am-menu-toggle]'))) {
      const open = $('#am-menu').classList.toggle('is-open');
      el.setAttribute('aria-expanded', open);
    } else if ((el = hit('[data-am-thumb]'))) {
      setImage(el.dataset.amThumb, el);
    } else if ((el = hit('[data-am-sticky-add]'))) {
      $('[data-am-product-form] [type="submit"]')?.click();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) cart.close();
  });

  /* ---------- Product page ---------- */
  const form = $('[data-am-product-form]');
  const product = JSON.parse($('#am-product-json')?.textContent || 'null');
  const stageImg = $('#am-main-image');

  function setImage(src, thumb) {
    if (!stageImg || !src) return;
    stageImg.removeAttribute('srcset');
    stageImg.src = src;
    stageImg.classList.remove('is-swap');
    void stageImg.offsetWidth;
    stageImg.classList.add('is-swap');
    $$('[data-am-thumb]').forEach((t) => t.setAttribute('aria-current', t === thumb));
  }

  if (form && product) {
    const idInput = $('[name="id"]', form);
    const addBtn = $('[type="submit"]', form);
    const qtyInput = $('[data-am-qty-input]', form);
    const error = $('[data-am-form-error]', form);

    // Any quantity can be picked; Shopify keeps only what's in stock, seen in the cart and at checkout
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      error.textContent = '';
      const id = idInput.value;
      const want = Number($('[name="quantity"]', form)?.value) || 1;
      flyToCart(stageImg || $('.am-product__stage svg'));
      try {
        await withLoading(addBtn, async () => {
          if (!cart.data) await cart.refresh();
          const already = inCart(id).reduce((n, i) => n + i.quantity, 0);
          cart.render(await api('cart/update.js', { updates: { [id]: already + want } }));
        });
      } catch (err) {
        error.textContent = err.message;
      }
    });

    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-am-option]')) return;
      const chosen = $$('[data-am-option]:checked', form).map((i) => i.value);
      const variant = product.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      $$('[data-am-option]:checked', form).forEach((i) => {
        const label = $('[data-am-option-label]', i.closest('fieldset'));
        if (label) label.textContent = i.value;
      });

      const available = !!variant?.available;
      addBtn.disabled = !available;
      addBtn.textContent = !variant ? 'Unavailable' : available ? 'Add to cart' : 'Out of stock';
      $$('[data-am-sticky-add]').forEach((b) => (b.disabled = !available));
      $$('[data-am-stock]').forEach((s) => {
        s.classList.toggle('is-out', !available);
        $('span', s).textContent = available ? s.dataset.in : 'Out of stock';
      });
      if (qtyInput) qtyInput.removeAttribute('max');
      if (!variant) return;

      idInput.value = variant.id;
      $$('[data-am-price]').forEach((el) => {
        el.innerHTML = priceHTML(variant.price, variant.compare_at_price || 0, { save: true });
        $('.am-price', el)?.classList.add('am-price--lg');
      });
      if (variant.featured_image?.src) setImage(variant.featured_image.src);
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    });

    // Mobile sticky buy bar appears once the main buttons scroll away
    const sticky = $('[data-am-sticky]');
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-on', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe($('.am-buy__actions', form) || form);
    }
  }

  /* ---------- Category slider: slow endless scroll ---------- */
  $$('[data-am-marquee]').forEach((nav) => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const track = $('[data-am-catnav]', nav);
    const items = [...track.children];
    if (!items.length) return;
    const belt = document.createElement('div');
    belt.className = 'am-catnav__belt';
    items.forEach((el) => belt.append(el));
    track.append(belt);
    const build = () => {
      belt.classList.remove('is-running');
      [...belt.children].slice(items.length).forEach((el) => el.remove());
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const setWidth = belt.scrollWidth + gap;
      // enough copies to cover the screen twice, so the loop never shows a gap
      const copies = Math.max(2, Math.ceil((track.clientWidth * 2) / setWidth) + 1);
      for (let c = 1; c < copies; c++) {
        items.forEach((el) => {
          const clone = el.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.tabIndex = -1;
          belt.append(clone);
        });
      }
      belt.style.setProperty('--belt-shift', `${setWidth}px`);
      belt.style.setProperty('--belt-time', `${setWidth / (Number(nav.dataset.amMarquee) || 25)}s`);
      void belt.offsetWidth;
      belt.classList.add('is-running');
    };
    build();
    let resizeTimer;
    addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 200);
    });
    // pause while a finger is on it
    track.addEventListener('touchstart', () => track.classList.add('is-held'), { passive: true });
    track.addEventListener('touchend', () => setTimeout(() => track.classList.remove('is-held'), 1200), { passive: true });
  });

  /* ---------- Category slider arrows ---------- */
  $$('[data-am-catnav]').forEach((track) => {
    if (track.closest('[data-am-marquee]')) return;
    const frame = track.parentElement;
    const prev = $('[data-am-catnav-prev]', frame);
    const next = $('[data-am-catnav-next]', frame);
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft < 4;
      next.hidden = max - track.scrollLeft < 4;
    };
    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.8 }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.8 }));
    track.addEventListener('scroll', update, { passive: true });
    addEventListener('resize', update);
    // keep the current category in view on category pages
    const active = $('.is-active', track);
    if (active) track.scrollLeft = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2;
    update();
  });

  /* ---------- "Goes great with": best tag matches first, shuffled within each level ---------- */
  const related = $('[data-am-related]');
  if (related) {
    const limit = Number(related.dataset.limit) || 4;
    const items = shuffle($$('.am-rel', related)).sort((a, b) => b.dataset.score - a.dataset.score);
    items.forEach((el, i) => {
      el.hidden = i >= limit;
      el.style.order = i;
      if (el.firstElementChild) el.firstElementChild.style.order = i;
    });
  }

  /* ---------- Collection sorting ---------- */
  $('[data-am-sort]')?.addEventListener('change', (e) => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    location.href = url;
  });

  /* ---------- Typo-tolerant search ---------- */
  const searchRoot = $('[data-am-search]');
  if (searchRoot) {
    const norm = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const squash = (t) => t.replace(/ /g, '');

    // Edit distance that also counts swapped neighbours ("chagrer") as one typo
    const distance = (a, b) => {
      const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
      for (let j = 1; j <= b.length; j++) d[0][j] = j;
      for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
          if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
      }
      return d[a.length][b.length];
    };
    const allowed = (word) => (word.length <= 3 ? 0 : word.length <= 5 ? 1 : 2);

    // Everyday words people type -> words used in the catalogue
    const SYNONYMS = {
      garbage: ['bin', 'trash'], rubbish: ['bin', 'trash'], waste: ['bin', 'trash'], trash: ['bin'], bin: ['trash'],
      organiser: ['organizer', 'storage'], storage: ['organizer'],
      mount: ['holder', 'stand'], stand: ['holder'], holder: ['stand', 'mount'],
      freshener: ['freshner', 'fragrance', 'scent'], perfume: ['fragrance', 'freshner'], smell: ['fragrance', 'freshner'],
      cleaner: ['clean', 'cleaning'], cleaning: ['clean'], wash: ['clean', 'washer'], hoover: ['vacuum'],
      plug: ['charger'], lighter: ['charger'], power: ['charger'], adapter: ['charger'],
      aux: ['bluetooth', 'music'], radio: ['bluetooth', 'music'], fm: ['bluetooth'], music: ['bluetooth'],
      mobile: ['phone'], cell: ['phone'], fone: ['phone'], fon: ['phone'],
      iphone: ['phone', 'charging', 'charger'], samsung: ['phone', 'charging', 'charger'], android: ['phone', 'charging', 'charger'],
      cord: ['cable'], wire: ['cable'], antitheft: ['lock'], security: ['lock'], theft: ['lock'],
      pet: ['dog'], puppy: ['dog'], glasses: ['sunglasses'], shades: ['sunglasses'],
      napkin: ['tissue'], tissues: ['tissue'], hanger: ['hook'], bag: ['hook'], boot: ['trunk'],
      cigarette: ['ashtray'], smoking: ['ashtray'],
    };
    const SYN_KEYS = Object.keys(SYNONYMS);
    const variants = (token) => {
      const out = new Map([[token, 1]]);
      const keys = SYN_KEYS.filter((k) => k === token || (allowed(token) && Math.abs(k.length - token.length) <= 2 && distance(token, k) <= allowed(token)));
      keys.forEach((k) => {
        if (!out.has(k)) out.set(k, 0.9);
        SYNONYMS[k].forEach((w) => out.has(w) || out.set(w, 0.8));
      });
      return out;
    };

    // 0 = no match, 3 = exact or prefix match
    const wordScore = (token, words, joined) => {
      if (joined.includes(token)) return 3;
      let best = 0;
      for (const w of words) {
        if (w.startsWith(token)) return 3;
        const max = allowed(token);
        if (!max) continue;
        const d = Math.min(distance(token, w), distance(token, w.slice(0, token.length)) + 0.5);
        if (d <= max) best = Math.max(best, 2 - d / (max + 1));
      }
      return best;
    };

    const field = (text) => {
      const t = norm(text || '');
      return { words: t.split(' ').filter(Boolean), joined: squash(t) };
    };
    const items = $$('[data-am-search-item]', searchRoot).map((el, i) => ({
      el, i, title: field(el.dataset.searchTitle), extra: field(el.dataset.search),
    }));

    // Title matches count fully; type and tags count a little less
    const tokenScore = (alts, item) => {
      let best = 0;
      alts.forEach((weight, alt) => {
        const s = Math.max(wordScore(alt, item.title.words, item.title.joined), 0.6 * wordScore(alt, item.extra.words, item.extra.joined));
        best = Math.max(best, s * weight);
      });
      return best;
    };

    const input = $('[data-am-search-input]', searchRoot);
    const status = $('[data-am-search-status]', searchRoot);
    const empty = $('[data-am-search-empty]', searchRoot);

    const run = () => {
      const q = norm(input.value);
      const tokens = q ? q.split(' ') : [];
      const alts = tokens.map(variants);
      const scored = items.map((item) => {
        if (!tokens.length) return { item, score: 1, all: true };
        const parts = alts.map((a) => tokenScore(a, item));
        // also try the whole query glued together ("seat belt" vs "seatbelt")
        const glued = tokens.length > 1 && (item.title.joined.includes(squash(q)) || item.extra.joined.includes(squash(q))) ? 3 * tokens.length : 0;
        const score = Math.max(parts.reduce((a, b) => a + b, 0), glued);
        return { item, score, all: glued > 0 || parts.every((p) => p > 0) };
      });
      // Prefer products matching every word; fall back to any word; drop weak tail matches
      let shown = scored.filter((s) => s.all && s.score > 0);
      if (!shown.length) shown = scored.filter((s) => s.score > 0);
      const top = Math.max(0, ...shown.map((s) => s.score));
      shown = shown.filter((s) => s.score >= top * 0.55);
      const exact = shown.every((s) => !tokens.length || s.score >= 3 * tokens.length);
      shown.sort((a, b) => b.score - a.score || a.item.i - b.item.i);

      const visible = new Set(shown.map((s) => s.item));
      items.forEach((item) => (item.el.hidden = !visible.has(item)));
      shown.forEach((s, order) => {
        const card = s.item.el.firstElementChild;
        if (card) {
          card.style.order = order;
          card.classList.add('is-in');
        }
      });
      empty.hidden = shown.length > 0;
      status.textContent = !tokens.length
        ? `${items.length} products`
        : shown.length
          ? `${shown.length} result${shown.length === 1 ? '' : 's'} for “${input.value.trim()}”${exact ? '' : ' (closest matches)'}`
          : '';
    };

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        run();
        const url = new URL(location.href);
        input.value.trim() ? url.searchParams.set('q', input.value.trim()) : url.searchParams.delete('q');
        history.replaceState({}, '', url);
      }, 120);
    });
    $('form', searchRoot).addEventListener('submit', (e) => {
      e.preventDefault();
      run();
    });
    if (!input.value) input.value = new URL(location.href).searchParams.get('q') || '';
    run();
  }

  /* ---------- Scroll reveal ---------- */
  const reveals = $$('[data-reveal]');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-in'));
  }

  cart.refresh().catch(() => {});
  window.AutoMood = { cart, money };
})();
